import { Link } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { OutlookAccountFormSelector } from "modules/campaigns/components/Sections/triggers/outlook/OutlookAccountFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { getOutlookIntegrationIdsFromTriggerIntegration } from "schema"
import { z } from "zod"
import { OpeningEmailForm } from "./OpeningEmailForm"
import {
  clearCampaignTriggerEmail,
  getCampaignTriggerEmail,
  replaceCampaignTriggerEmail,
} from "./campaignTriggerEmail"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type Outlook = Extract<NonNullable<FormValues["triggerIntegration"]>, { type: "outlook" }>

export function OutlookChannelForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const outlookIntegrationIds = getOutlookIntegrationIdsFromTriggerIntegration(triggerIntegration)
  const outlook = triggerIntegration?.type === "outlook" ? triggerIntegration : undefined
  const emailIntegration = getCampaignTriggerEmail(triggerIntegration)

  const buildOutlook = (patch: Partial<Outlook>) =>
    replaceCampaignTriggerEmail(triggerIntegration, {
      type: "outlook",
      outlookIntegrationId: outlookIntegrationIds[0],
      outlookIntegrationIds,
      delayInDays: outlook?.delayInDays ?? undefined,
      ...(emailIntegration?.type === "outlook" ? emailIntegration : {}),
      ...patch,
    })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => {
          return (
            <div>
              <FormItem className="flex flex-col gap-2">
                <FormLabel>{t("campaigns.trigger.metadata.outlookInbox.selectAccount")}</FormLabel>
                <FormControl>
                  <OutlookAccountFormSelector
                    values={outlookIntegrationIds}
                    emptyPlaceholder={t("campaigns.trigger.metadata.outlookInbox.emptyPlaceholder")}
                    placeholder={t("campaigns.trigger.metadata.outlookInbox.emptyPlaceholder")}
                    searchPlaceholder={t(
                      "campaigns.trigger.metadata.outlookInbox.searchPlaceholder",
                    )}
                    onChangeMany={(accounts) => {
                      const [firstAccount] = accounts
                      field.onChange(
                        firstAccount
                          ? buildOutlook({
                              outlookIntegrationId: firstAccount.value,
                              outlookIntegrationIds: accounts.map((account) => account.value),
                            })
                          : clearCampaignTriggerEmail(triggerIntegration),
                        { shouldDirty: true },
                      )
                    }}
                    hasCreateNew
                    isDisabled={isDisabled}
                  />
                </FormControl>
                <FormMessage />
                <span className="text-sm text-muted-foreground">
                  <Trans
                    i18nKey={"campaigns.trigger.metadata.outlookInbox.accountDescription"}
                    components={{
                      a: (
                        <Link
                          className="underline text-muted-foreground"
                          to="/settings/channels/outlook"
                          target="_blank"
                        />
                      ),
                    }}
                  />
                </span>
              </FormItem>
            </div>
          )
        }}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormLabel>
                {t("campaigns.trigger.metadata.outlookInbox.disableAfterHumanInteraction.title")}
              </FormLabel>
              <FormControl>
                <div className="flex items-start gap-3">
                  <span className="text-muted-foreground text-xs block flex-1">
                    {t(
                      "campaigns.trigger.metadata.outlookInbox.disableAfterHumanInteraction.description",
                    )}
                  </span>
                  <Input
                    wrapperClassName="w-auto"
                    className="w-24 shrink-0"
                    disabled={isDisabled}
                    value={outlook?.delayInDays ?? ""}
                    min={1}
                    type="number"
                    placeholder={t(
                      "campaigns.trigger.metadata.outlookInbox.disableAfterHumanInteraction.placeholder",
                    )}
                    onChange={(value) => {
                      const delayInDays = value.target.value
                        ? Number(value.target.value)
                        : undefined

                      field.onChange(buildOutlook({ delayInDays }), { shouldDirty: true })
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
      <OpeningEmailForm isDisabled={isDisabled} type="outlook" />
    </>
  )
}
